import { Get } from "@/src/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/src/components/ui/dialog";
import { ScrollArea } from "@/src/components/ui/scroll-area";
import { RewardHistoryItem } from "@/src/views/admin/user/RewardHistoryItem";
import { useInfiniteQuery } from "@tanstack/react-query";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/ui/select";
import { UserInstalledGame, UserTotalCoinsPerGame } from "@/src/types/user";
import { Button } from "@/src/components/ui/button";
import { FaCoins } from "react-icons/fa";
import { Input } from "@/src/components/ui/input";
import { Search } from "lucide-react";

export interface RewardHistoryItemType {
  changeType: string;
  campaignId: number | null;
  coinChange: number;
  appTitle: string | null;
  packageName: string | null;
  campaignName: string | null;
  partnerId: number | null;
  eventName: string | null;
  happenedAt: string;
  supportNote: string | null;
}

interface RewardHistoryModalProps {
  userID: string;
  installedGames: UserInstalledGame[];
  userTotalCoinsPerGame: UserTotalCoinsPerGame[] | null;
}

export interface RewardHistoryModalRef {
  openModal: () => void;
}

const GENERAL_FILTER_VALUE = "__general__";

const fetchRewardHistory = async ({
  pageParam = null,
  userId = "",
  packageName = null,
  appTitle = null,
}: {
  pageParam: any;
  userId: string;
  packageName: string | null;
  appTitle: string | null;
}) => {
  const baseUrl = `/support/user/${userId}/reward-history`;
  let url = pageParam
    ? `${baseUrl}?lastEntry=${pageParam}&limit=10`
    : `${baseUrl}?limit=10`;

  if (appTitle) {
    url += `${url.includes("?") ? "&" : "?"}appTitle=${appTitle}`;
  } else if (packageName) {
    url += `${url.includes("?") ? "&" : "?"}packageName=${packageName}`;
  }

  return Get(url);
};

const RewardHistoryModal = forwardRef<
  RewardHistoryModalRef,
  RewardHistoryModalProps
>(({ userID, installedGames, userTotalCoinsPerGame }, ref) => {
  const { ref: viewRef, inView } = useInView();
  const dialogTriggerRef = useRef<HTMLButtonElement>(null);
  const [selectedGame, setSelectedGame] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const getCoinValueForGame = useCallback(
    (packageName: string | null): number | null => {
      if (!userTotalCoinsPerGame || !packageName) return null;
      const gameCoins = userTotalCoinsPerGame.find(
        (item) => item.packageName === packageName,
      );
      return gameCoins?.totalCoins ?? null;
    },
    [userTotalCoinsPerGame],
  );

  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["reward-history", userID, selectedGame],
    queryFn: async ({ pageParam }) => {
      const isGeneralFilter = selectedGame === GENERAL_FILTER_VALUE;
      const r = await fetchRewardHistory({
        pageParam: pageParam,
        userId: userID,
        packageName: isGeneralFilter ? null : selectedGame || null,
        appTitle: isGeneralFilter ? "general" : null,
      });

      return {
        data: r?.userRewardHistory || [],
        nextCursor: r?.nextCursor,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined,
    retry: 2,
  });

  const totalItems = useMemo(() => {
    if (!data?.pages) return 0;
    return data.pages.reduce((acc, page) => {
      if (!page.data) return acc;
      return acc + page.data.length;
    }, 0);
  }, [data]);

  const openModal = useCallback(() => {
    dialogTriggerRef.current?.click();
  }, []);

  useImperativeHandle(ref, () => ({
    openModal,
  }));

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage]);

  const handleGameChange = useCallback((value: string) => {
    setSelectedGame(value);
  }, []);

  const handleClearFilter = useCallback(() => {
    setSelectedGame("");
    setSearchQuery("");
  }, []);

  const filteredGames = useMemo(() => {
    if (!searchQuery) return installedGames;
    const query = searchQuery.toLowerCase();
    return installedGames.filter(
      (game) =>
        game.name.toLowerCase().includes(query) ||
        game.packageName?.toLowerCase().includes(query),
    );
  }, [installedGames, searchQuery]);

  return (
    <Dialog>
      <DialogTrigger className="hidden" ref={dialogTriggerRef} />
      <DialogContent
        aria-describedby="reward-history-modal"
        aria-description="user reward history modal"
      >
        <DialogHeader>
          <DialogTitle>Reward History</DialogTitle>
        </DialogHeader>

        <div className="flex w-full mb-4">
          <div className="w-3/4 mr-2">
            <Select
              onValueChange={(value) => {
                handleGameChange(value);
                setSearchQuery("");
              }}
              value={selectedGame}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filter by game" />
              </SelectTrigger>
              <SelectContent onCloseAutoFocus={() => setSearchQuery("")}>
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search games..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                <SelectItem value={GENERAL_FILTER_VALUE}>General</SelectItem>

                {filteredGames.length > 0 ? (
                  filteredGames.map((game, index) => {
                    const gameId = game?.packageName || `game-${index}`;
                    const coinValue = getCoinValueForGame(game.packageName);

                    return (
                      <SelectItem key={gameId} value={gameId}>
                        <span className="flex flex-row items-center">
                          {game.name.trim()}
                          <span className="text-sm ml-2 flex flex-row items-center">
                            {"("}
                            {coinValue?.toLocaleString() ?? "0"}{" "}
                            <FaCoins className="text-yellow-300 text-sm ml-2" />
                            {")"}
                          </span>
                        </span>
                      </SelectItem>
                    );
                  })
                ) : (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    No games found
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>
          {selectedGame && (
            <Button
              variant="outline"
              className="w-1/4"
              onClick={handleClearFilter}
            >
              Clear
            </Button>
          )}
        </div>

        <div className="flex flex-1 w-full flex-col items-center relative">
          <ScrollArea className="h-[70vh] w-full overflow-y-auto pr-4">
            {data?.pages && totalItems > 0 ? (
              data.pages.map((page, i) => (
                <React.Fragment key={i}>
                  {page.data &&
                    page.data.map(
                      (reward: RewardHistoryItemType, index: number) => (
                        <RewardHistoryItem
                          key={`${userID}-reward-${index}`}
                          reward={reward}
                        />
                      ),
                    )}
                </React.Fragment>
              ))
            ) : (
              <div className="flex flex-col flex-1 h-[70vh] items-center justify-center">
                <p className="text-base text-gray-600">
                  No reward found for user with ID: {userID}
                  {selectedGame === GENERAL_FILTER_VALUE
                    ? " (General)"
                    : selectedGame
                      ? ` and game: ${selectedGame}`
                      : ""}
                </p>
              </div>
            )}
            {hasNextPage && <div ref={viewRef} className="h-10 -mt-24" />}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
});

export default RewardHistoryModal;
